/**
 * Red→green heatmap palette.
 *
 * The 8-bucket gain-pct palette below is the canonical OL heatmap; it was
 * extracted from utils/elasticityCalculations.js (formerly getPremiumGainColor)
 * so that both the watchlist heatmap and the new scoring builder share one
 * source of truth.
 *
 * Three consumer functions live here:
 *   - getColorForGainPct(pct):  premium-gain % heatmap (-100..+200+)
 *   - getMultiplierColor(value): green-only gradient for option multipliers
 *   - getColorForScore10(score): 1-10 scoring rubric heatmap (sampled from
 *     the same red→green tiers)
 */

// Ordered from highest to lowest. First match wins.
export const GAIN_PCT_TIERS = [
  { min: 200,  backgroundColor: '#085041', color: '#ffffff' }, // dark green
  { min: 100,  backgroundColor: '#0F6E56', color: '#ffffff' },
  { min: 50,   backgroundColor: '#1D9E75', color: '#ffffff' },
  { min: 20,   backgroundColor: '#5DCAA5', color: '#000000' },
  { min: 0,    backgroundColor: '#9FE1CB', color: '#000000' }, // light green
  { min: -30,  backgroundColor: '#D3D1C7', color: '#000000' }, // gray
  { min: -60,  backgroundColor: '#F09595', color: '#000000' }, // light red
  { min: -Infinity, backgroundColor: '#791F1F', color: '#ffffff' }, // dark red
];

// 1-10 score palette. Mirrors the same red→green tone progression as
// GAIN_PCT_TIERS, mapped onto the 1-10 score domain so scores feel native.
// 5.5 is the neutral midpoint (gray).
export const SCORE_10_TIERS = [
  { min: 9.0, backgroundColor: '#085041', color: '#ffffff' }, // 9-10
  { min: 8.0, backgroundColor: '#0F6E56', color: '#ffffff' }, // 8-9
  { min: 7.0, backgroundColor: '#1D9E75', color: '#ffffff' }, // 7-8
  { min: 6.0, backgroundColor: '#5DCAA5', color: '#000000' }, // 6-7
  { min: 5.0, backgroundColor: '#9FE1CB', color: '#000000' }, // 5-6
  { min: 4.0, backgroundColor: '#D3D1C7', color: '#000000' }, // 4-5  neutral
  { min: 3.0, backgroundColor: '#F09595', color: '#000000' }, // 3-4
  { min: -Infinity, backgroundColor: '#791F1F', color: '#ffffff' }, // 1-3
];

/**
 * Return a {backgroundColor, color} style for a value against a tier table.
 * Returns an empty object when value is null / NaN so consumers can spread
 * unconditionally.
 */
export function getColorForValue(value, tiers) {
  if (value == null || Number.isNaN(value)) return {};
  for (const tier of tiers) {
    if (value >= tier.min) {
      return { backgroundColor: tier.backgroundColor, color: tier.color };
    }
  }
  return {};
}

/** Premium-gain % heatmap — the original WatchlistHeatmapView coloring. */
export function getColorForGainPct(pct) {
  return getColorForValue(pct, GAIN_PCT_TIERS);
}

/** 1-10 scoring rubric heatmap — used by the Scoring Builder. */
export function getColorForScore10(score) {
  return getColorForValue(score, SCORE_10_TIERS);
}

/**
 * Green-only multiplier gradient. Kept here for consolidation; consumers
 * still import the wrapper from elasticityCalculations.
 */
export function getMultiplierColor(value) {
  if (value == null || Number.isNaN(value) || value <= 0) return {};
  if (value < 2)  return { backgroundColor: 'rgba(34, 197, 94, 0.1)' };
  if (value < 4)  return { backgroundColor: 'rgba(34, 197, 94, 0.2)' };
  if (value < 6)  return { backgroundColor: 'rgba(34, 197, 94, 0.3)' };
  if (value < 10) return { backgroundColor: 'rgba(34, 197, 94, 0.4)' };
  if (value < 20) return { backgroundColor: 'rgba(34, 197, 94, 0.5)' };
  return { backgroundColor: 'rgba(34, 197, 94, 0.6)' };
}
